import { IconButton, TextField } from '@mui/material'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { downloadCSVFile, getCSVContent } from '../../../../utils/csv'
import PageLoading from '../../../../components/PageLoading/PageLoading'
import InformationTable from '../../../../components/Table/InformationTable'
import TableHeader from '../../../../components/TableHeader/TableHeader'
import { useNotification } from '../../../../context/NotificationManager/NotificationManager'
import MesRecienteSelector from '../../../../components/form/MesRecienteSelector/MesRecienteSelector'
import { dateToString, monthToString } from '../../../../utils/date'
import { textFilter } from '../../../../utils/table'
import {
  deleteOportunidad,
  getOportunidadesMarketing,
  resincronizarOportunidades
} from '../../../../utils/api/oportunidadesMarketing'
import ModalConfirmacion from '../../../../modals/ModalConfirmacion/ModalConfirmacion'
import css from './Oportunidades.module.css'

const getEstadoLabel = estado => {
  const estados = {
    ABIERTA: 'Abierta',
    CERRADA: 'Cerrada',
    DESCARTADA: 'Descartada'
  }
  return estados[estado] || estado
}

const getTipoLabel = tipo => {
  const tipos = {
    LLAMADA: 'Llamada',
    FORMULARIO: 'Formulario',
    WHATSAPP: 'WhatsApp'
  }
  return tipos[tipo] || tipo
}

const getAsignadaLabel = asignada => (asignada ? 'Sí' : 'No')

const Oportunidades = () => {
  const notification = useNotification()
  const queryClient = useQueryClient()

  const [mes, setMes] = useState(monthToString(new Date()))
  const [search, setSearch] = useState('')
  const [modalDeleteOportunidad, setModalDeleteOportunidad] = useState(false)
  const [modalResincronizar, setModalResincronizar] = useState(false)
  const [modalDetalles, setModalDetalles] = useState(false)
  const [oportunidadIdToDelete, setOportunidadIdToDelete] = useState(null)
  const [oportunidadSeleccionada, setOportunidadSeleccionada] = useState(null)

  const { isLoading: isLoadingOportunidades, data: oportunidadesList = [] } =
    useQuery({
      queryKey: ['oportunidades-marketing', 'list', mes],
      queryFn: () =>
        getOportunidadesMarketing({ mes })
          .then(datos => datos)
          .catch(err => {
            notification.error({
              title: 'Error al recuperar las oportunidades',
              content: err
            })
            return []
          })
    })

  const { isFetching: isDeleting, mutate: borrarOportunidad } = useMutation({
    mutationFn: deleteOportunidad,
    onSuccess: () => {
      notification.success({
        title: 'Oportunidad borrada',
        content: 'La oportunidad ha sido borrada correctamente'
      })
      queryClient.invalidateQueries(['oportunidades-marketing', 'list', mes])
      setModalDeleteOportunidad(false)
      setOportunidadIdToDelete(null)
    },
    onError: err => {
      notification.error({
        title: 'Error al borrar la oportunidad',
        content: err
      })
      setModalDeleteOportunidad(false)
      setOportunidadIdToDelete(null)
    }
  })

  const { isFetching: isResyncing, mutate: resincronizar } = useMutation({
    mutationFn: resincronizarOportunidades,
    onSuccess: () => {
      notification.success({
        title: 'Resincronización completada',
        content: 'Las oportunidades han sido resincronizadas correctamente'
      })
      queryClient.invalidateQueries(['oportunidades-marketing', 'list', mes])
      setModalResincronizar(false)
    },
    onError: err => {
      notification.error({
        title: 'Error al resincronizar',
        content: err
      })
      setModalResincronizar(false)
    }
  })

  const handleExport = () => {
    const content = getCSVContent({
      columns: [
        'Fecha',
        'Tipo',
        'Teléfono Llamada',
        'Nombre',
        'Teléfono',
        'Email',
        'GCLID',
        'FBCLID',
        'TIKCLID',
        'Centro',
        'Estado',
        'Asignado a llamada'
      ],
      data: oportunidadesList,
      transform: oportunidad => [
        dateToString(new Date(oportunidad.fecha)),
        getTipoLabel(oportunidad.tipo),
        oportunidad.telefonoLlamada || '-',
        oportunidad.nombre || '-',
        oportunidad.telefono || '-',
        oportunidad.email || '-',
        oportunidad.gclid || '-',
        oportunidad.fbclid || '-',
        oportunidad.tikclid || '-',
        oportunidad.centro || '-',
        getEstadoLabel(oportunidad.estado),
        getAsignadaLabel(oportunidad.asignada)
      ]
    })
    downloadCSVFile(content, 'oportunidades-marketing')
  }

  const handleOpenDeleteModal = id => {
    setOportunidadIdToDelete(id)
    setModalDeleteOportunidad(true)
  }

  const handleCloseDeleteModal = () => {
    if (isDeleting) return
    setModalDeleteOportunidad(false)
    setOportunidadIdToDelete(null)
  }

  const handleConfirmDelete = () => {
    if (!oportunidadIdToDelete || isDeleting) return
    borrarOportunidad(oportunidadIdToDelete)
  }

  const handleOpenResincronizarModal = () => {
    setModalResincronizar(true)
  }

  const handleCloseResincronizarModal = () => {
    if (isResyncing) return
    setModalResincronizar(false)
  }

  const handleConfirmResincronizar = () => {
    if (isResyncing) return
    resincronizar()
  }

  const handleOpenDetallesModal = oportunidad => {
    setOportunidadSeleccionada(oportunidad)
    setModalDetalles(true)
  }

  const handleCloseDetallesModal = () => {
    setModalDetalles(false)
  }

  const filterSearch = oportunidad => {
    return textFilter({
      object: oportunidad,
      fields: [
        'tipo',
        'nombre',
        'telefono',
        'telefonoLlamada',
        'centro',
        'estado'
      ],
      search: search
    })
  }

  return (
    <PageLoading isLoading={isLoadingOportunidades}>
      <TableHeader
        actions={
          <div className={css.actions}>
            <IconButton
              className={css.resync}
              size='small'
              color='warning'
              onClick={handleOpenResincronizarModal}
              title='Resincronizar oportunidades'
            >
              <i className='material-icons'>sync</i>
            </IconButton>
            <IconButton
              className={css.download}
              size='small'
              color='primary'
              onClick={handleExport}
            >
              <i className='material-icons'>download</i>
            </IconButton>
            <MesRecienteSelector
              className={css.mes}
              name='mes'
              label='Mes'
              value={mes}
              onChange={e => setMes(e.target.value)}
            />
          </div>
        }
        title='Oportunidades de Marketing'
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
      />
      <InformationTable
        details={[
          { title: 'Fecha', key: 'fecha', align: 'left' },
          { title: 'Nombre', key: 'nombre', align: 'left' },
          { title: 'Estado', key: 'estado', align: 'left' },
          {
            title: 'Teléfono Llamada',
            key: 'telefonoLlamada',
            sortDisabled: true,
            align: 'left'
          },
          { title: 'Centro', key: 'centro', align: 'left' },
          {
            title: 'Asignado',
            key: 'asignada',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Opciones',
            key: 'opciones',
            sortDisabled: true,
            align: 'center'
          }
        ]}
        data={oportunidadesList.filter(filterSearch).map(oportunidad => ({
          fecha: dateToString(new Date(oportunidad.fecha)),
          nombre: oportunidad.nombre || '-',
          estado: <strong>{getEstadoLabel(oportunidad.estado)}</strong>,
          telefonoLlamada:
            oportunidad.telefonoLlamada || oportunidad.telefono || '-',
          centro: oportunidad.centro || '-',
          asignada: getAsignadaLabel(oportunidad.asignada),
          opciones: (
            <>
              <IconButton
                size='small'
                color='primary'
                onClick={() => handleOpenDetallesModal(oportunidad)}
                title='Ver detalles'
              >
                <i className='material-icons'>visibility</i>
              </IconButton>
              <IconButton
                size='small'
                color='error'
                onClick={() => handleOpenDeleteModal(oportunidad.id)}
                title='Borrar oportunidad'
              >
                <i className='material-icons'>delete</i>
              </IconButton>
            </>
          )
        }))}
      />
      <ModalConfirmacion
        open={modalDeleteOportunidad}
        onClose={handleCloseDeleteModal}
        onClick={handleConfirmDelete}
        title='Confirmar borrado de oportunidad'
        content={
          <>
            <p>Estás a punto de borrar esta oportunidad de marketing</p>
            <p>
              Esta operación es <strong>irreversible</strong>. La oportunidad
              desaparecerá permanentemente y no se podrá recuperar la
              información.
            </p>
          </>
        }
        okText='Borrar oportunidad'
      />
      <ModalConfirmacion
        open={modalResincronizar}
        onClose={handleCloseResincronizarModal}
        onClick={handleConfirmResincronizar}
        title='Confirmar resincronización'
        content={
          <>
            <p>
              Estás a punto de resincronizar todas las oportunidades de
              marketing
            </p>
            <p>
              Esta acción se ejecuta sobre <strong>todas</strong> las
              oportunidades de marketing, no solo sobre la sede y mes
              seleccionados.
            </p>
            <p>
              El proceso vinculará las oportunidades abiertas con las llamadas
              correspondientes.
            </p>
          </>
        }
        okText='Resincronizar'
      />
      <ModalConfirmacion
        open={modalDetalles}
        onClose={handleCloseDetallesModal}
        title='Detalles de la Oportunidad'
        maxWidth='md'
        content={
          oportunidadSeleccionada ? (
            <div className={css.main}>
              <section className={css.section}>
                <TextField
                  className={css.input}
                  label='Fecha'
                  value={dateToString(new Date(oportunidadSeleccionada.fecha))}
                  InputProps={{ readOnly: true }}
                  variant='standard'
                />
                <TextField
                  className={css.input}
                  label='Estado'
                  value={getEstadoLabel(oportunidadSeleccionada.estado)}
                  InputProps={{ readOnly: true }}
                  variant='standard'
                />
                <TextField
                  className={css.input}
                  label='Asignado'
                  value={getAsignadaLabel(oportunidadSeleccionada.asignada)}
                  InputProps={{ readOnly: true }}
                  variant='standard'
                />
              </section>
              <section className={css.section}>
                <TextField
                  className={css.input}
                  label='Tipo'
                  value={getTipoLabel(oportunidadSeleccionada.tipo)}
                  InputProps={{ readOnly: true }}
                  variant='standard'
                />
                <TextField
                  className={css.input}
                  label='Teléfono Llamada'
                  value={oportunidadSeleccionada.telefonoLlamada || ''}
                  InputProps={{ readOnly: true }}
                  variant='standard'
                />
              </section>
              <section className={css.section}>
                <TextField
                  className={css.input}
                  label='Nombre'
                  value={oportunidadSeleccionada.nombre || ''}
                  InputProps={{ readOnly: true }}
                  variant='standard'
                />
                <TextField
                  className={css.input}
                  label='Centro'
                  value={oportunidadSeleccionada.centro || ''}
                  InputProps={{ readOnly: true }}
                  variant='standard'
                />
              </section>
              <section className={css.section}>
                <TextField
                  className={css.input}
                  label='Teléfono'
                  value={oportunidadSeleccionada.telefono || ''}
                  InputProps={{ readOnly: true }}
                  variant='standard'
                />
                <TextField
                  className={css.input}
                  label='Email'
                  value={oportunidadSeleccionada.email || ''}
                  InputProps={{ readOnly: true }}
                  variant='standard'
                />
              </section>
              <section className={css.section}>
                <TextField
                  className={css.input}
                  label='Origen'
                  value={oportunidadSeleccionada.origen || ''}
                  InputProps={{ readOnly: true }}
                  variant='standard'
                />
                <TextField
                  className={css.input}
                  label='Anuncio'
                  value={oportunidadSeleccionada.anuncio || ''}
                  InputProps={{ readOnly: true }}
                  variant='standard'
                />
                <TextField
                  className={css.input}
                  label='Segmento'
                  value={oportunidadSeleccionada.segmento || ''}
                  InputProps={{ readOnly: true }}
                  variant='standard'
                />
              </section>
              <TextField
                className={css.input}
                label='GCLID'
                value={oportunidadSeleccionada.gclid || ''}
                InputProps={{ readOnly: true }}
                variant='standard'
              />
              <TextField
                className={css.input}
                label='FBCLID'
                value={oportunidadSeleccionada.fbclid || ''}
                InputProps={{ readOnly: true }}
                variant='standard'
              />
              <TextField
                className={css.input}
                label='TIKCLID'
                value={oportunidadSeleccionada.tikclid || ''}
                InputProps={{ readOnly: true }}
                variant='standard'
              />
              <TextField
                className={css.input}
                label='Observaciones'
                value={oportunidadSeleccionada.observaciones || ''}
                InputProps={{ readOnly: true }}
                variant='standard'
                multiline
              />
            </div>
          ) : null
        }
        okText='Cerrar'
        hideCancel
      />
    </PageLoading>
  )
}

export default Oportunidades
